import { forwardRef, type CSSProperties, type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { usePrefersReducedMotion } from '../../hooks/useFloating';
import { useDsT } from '../../services/i18n';

export type SkeletonShape = 'text' | 'rect' | 'circle';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  shape?: SkeletonShape;
  /** Number of text lines (text shape only). */
  lines?: number;
  width?: number | string;
  height?: number | string;
  /** Disable the shimmer animation. */
  animate?: boolean;
  /** Screen-reader announcement, e.g. "Loading orders". Visual skeleton is always aria-hidden. */
  label?: string;
}

/*
 * Design Direction v2: an inert `bg-track` base with the shared `zo-shimmer` sheen travelling across it.
 * `zo-shimmer` is defined in `styles/index.css` and is itself inert under `prefers-reduced-motion`; the class
 * is also dropped in JS for that preference, so the skeleton is still when the stylesheet is not present.
 */
const shimmer = 'zo-shimmer';
const skeletonBase = 'zo-skeleton bg-track';

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { shape = 'text', lines = 1, width, height, animate = true, label, className, style, ...rest },
  ref,
) {
  const reduced = usePrefersReducedMotion();
  const dims: CSSProperties = { ...style, width, height };
  const base = cn(skeletonBase, animate && !reduced && shimmer);
  let body;
  if (shape === 'circle') {
    body = <div aria-hidden="true" className={cn(base, 'rounded-full', !width && !height && 'size-8')} style={dims} />;
  } else if (shape === 'rect') {
    body = <div aria-hidden="true" className={cn(base, 'rounded-md', !height && 'h-20', !width && 'w-full')} style={dims} />;
  } else {
    const count = Math.max(1, lines);
    body = (
      <div aria-hidden="true" className="flex w-full flex-col gap-2" style={{ width }}>
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className={cn(base, 'h-3 rounded-sm', count > 1 && i === count - 1 ? 'w-3/5' : 'w-full')} style={{ height }} />
        ))}
      </div>
    );
  }
  return (
    <div ref={ref} data-shape={shape} className={cn('zo-skeleton-root', className)} {...rest}>
      {body}
      {label ? (
        <span role="status" className="sr-only">
          {label}
        </span>
      ) : null}
    </div>
  );
});

export interface SkeletonTableProps extends HTMLAttributes<HTMLDivElement> {
  rows?: number;
  cols?: number;
  /** Render a header row. */
  header?: boolean;
  label?: string;
}

/**
 * Column rhythm for the placeholder rows: the first column reads as an identifier, the middle columns as
 * descriptions and the last as a short value, so the skeleton is shaped like the data it stands in for
 * rather than being a grey block.
 */
const headerWidths = ['w-16', 'w-20', 'w-14', 'w-12', 'w-16', 'w-20'];
const cellWidths = [
  ['w-4/5', 'w-3/5', 'w-2/5', 'w-1/2', 'w-3/5', 'w-1/3'],
  ['w-3/5', 'w-4/5', 'w-1/3', 'w-2/5', 'w-1/2', 'w-1/2'],
  ['w-2/3', 'w-1/2', 'w-1/2', 'w-1/3', 'w-2/5', 'w-2/5'],
];

/** Placeholder for a table / grid while data loads; rows are shaped like real rows. */
export function SkeletonTable({ rows = 5, cols = 4, header = true, label: labelProp, className, ...rest }: SkeletonTableProps) {
  const t = useDsT();
  const label = labelProp ?? t('ds.skeleton.table', 'Loading table');
  const reduced = usePrefersReducedMotion();
  const row = (rowIdx: number, isHeader: boolean) => {
    const widths = isHeader ? headerWidths : cellWidths[rowIdx % cellWidths.length]!;
    return (
      <div
        key={rowIdx}
        data-skeleton-row={isHeader ? 'header' : rowIdx}
        className={cn('grid h-row items-center gap-3 px-cell', isHeader ? 'h-control border-b border-border-subtle' : 'border-b border-rule last:border-b-0')}
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: cols }, (_, c) => (
          <div
            key={c}
            className={cn('rounded-sm bg-track', isHeader ? 'h-2' : 'h-3', !isHeader && !reduced && shimmer, widths[c % widths.length])}
          />
        ))}
      </div>
    );
  };
  return (
    <div className={cn('zo-skeleton-table overflow-hidden rounded-lg bg-surface-1 shadow-1', className)} {...rest}>
      <div aria-hidden="true">
        {header ? row(-1, true) : null}
        {Array.from({ length: rows }, (_, r) => row(r, false))}
      </div>
      {label ? (
        <span role="status" className="sr-only">
          {label}
        </span>
      ) : null}
    </div>
  );
}
